import { CURRENT_SERIES_ID } from '@/config/app.config';
import { read } from './http';
import { MOCK_PERFORMANCES, MOCK_SCORING_RULES, mockTeamFixturePoints } from './mock/scoring.mock';
import type { PlayerPerformance, ScoringRules, TeamFixturePoints } from '@/types';

/**
 * Premier League fantasy scoring.
 *
 * The rates document and every performance row share the same action keys, so
 * a screen can walk one list and read `performance[key]` against `points[key]`
 * rather than knowing the rules in advance.
 */

/**
 * GET /api/v1/scoring/rules
 *
 * Public — no token needed, so the points system can be shown before sign-in.
 */
export function getScoringRules(signal?: AbortSignal): Promise<ScoringRules> {
  return read('/scoring/rules', () => MOCK_SCORING_RULES, signal);
}

/** GET /api/v1/scoring/fixtures/:fixtureId/performances */
export function getFixturePerformances(
  fixtureId: number,
  signal?: AbortSignal,
): Promise<readonly PlayerPerformance[]> {
  return read(
    `/scoring/fixtures/${fixtureId}/performances`,
    () => MOCK_PERFORMANCES.filter((row) => row.fixtureId === fixtureId),
    signal,
  );
}

/** GET /api/v1/scoring/fixtures/:fixtureId/top-scorers */
export function getFixtureTopScorers(
  fixtureId: number,
  limit = 5,
  signal?: AbortSignal,
): Promise<readonly PlayerPerformance[]> {
  return read(
    `/scoring/fixtures/${fixtureId}/top-scorers?limit=${limit}`,
    () =>
      [...MOCK_PERFORMANCES]
        .filter((row) => row.fixtureId === fixtureId)
        .sort((a, b) => b.totalPoints - a.totalPoints)
        .slice(0, limit),
    signal,
  );
}

/** GET /api/v1/scoring/players/:playerId/performances — one player's history. */
export function getPlayerPerformances(
  playerId: number,
  limit = 10,
  signal?: AbortSignal,
): Promise<readonly PlayerPerformance[]> {
  return read(
    `/scoring/players/${playerId}/performances?seriesId=${CURRENT_SERIES_ID}&limit=${limit}`,
    () => MOCK_PERFORMANCES.filter((row) => row.playerId === playerId),
    signal,
  );
}

/**
 * GET /api/v1/me/teams/:teamId/fixtures/:fixtureId/points
 *
 * Your eleven in one fixture, already scored. `appliedPoints` is what each
 * player was worth to you — double for the captain — so the screen never has
 * to apply the multiplier itself and risk disagreeing with the server.
 */
export function getTeamFixturePoints(
  teamId: string,
  fixtureId: number,
  signal?: AbortSignal,
): Promise<TeamFixturePoints> {
  return read(
    `/me/teams/${teamId}/fixtures/${fixtureId}/points`,
    () => mockTeamFixturePoints(teamId, fixtureId),
    signal,
  );
}
